'use client'

import { idmQueryKeys } from '@/api/momo/idm/idm-query-keys'
import { updateUser } from '@/api/momo/idm/users'
import { toastError, toastSuccess } from '@/components/toast/toast'
import { formatPhoneNumber } from '@/helpers/PhoneHelpers'
import { useLatestRef } from '@/hooks/use-latest-ref'
import {
  ChangePasswordCommand,
  CognitoIdentityProviderClient,
  NotAuthorizedException
} from '@aws-sdk/client-cognito-identity-provider'
import {
  Box,
  Button,
  Grid,
  Group,
  Paper,
  PasswordInput,
  Popover,
  Progress,
  rem,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import * as Sentry from '@sentry/nextjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { MasterPage } from '@/components/master'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Icon } from '@/components/icon'
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { StatusPageEmails } from './status-page-emails'
import { UpdateUserRequest } from '@/api/generated/model'

function PasswordRequirement({
  meets,
  label
}: {
  meets: boolean
  label: string
}) {
  return (
    <Text
      c={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      {meets ? (
        <Icon icon={faCheck} style={{ width: rem(14), height: rem(14) }} />
      ) : (
        <Icon icon={faXmark} style={{ width: rem(14), height: rem(14) }} />
      )}
      <Box ml={10}>{label}</Box>
    </Text>
  )
}

const requirements = [
  { re: /[0-9]/, label: 'Includes number' },
  { re: /[a-z]/, label: 'Includes lowercase letter' },
  { re: /[A-Z]/, label: 'Includes uppercase letter' },
  { re: /[$&+,:;=?@#|'<>.^*()%!-]/, label: 'Includes special symbol' }
]

function getStrength(password: string) {
  let multiplier = password.length > 5 ? 0 : 1

  requirements.forEach((requirement) => {
    if (!requirement.re.test(password)) {
      multiplier += 1
    }
  })

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10)
}

const cognitoIdentityProviderClient = new CognitoIdentityProviderClient({
  region: 'us-west-2'
})

export default function MyProfile() {
  const { account, session } = useAuthContext()
  const queryClient = useQueryClient()

  const hasLoggedSentryRef = useRef(false)
  useEffect(() => {
    if (!account || hasLoggedSentryRef.current) {
      return
    }

    Sentry.metrics.count('my_profile_page_viewed', 1, {
      attributes: {
        user_id: account.ID
      }
    })
    hasLoggedSentryRef.current = true
  }, [account])

  const [requirementsPopoverOpened, setRequirementsPopoverOpened] =
    useState(false)
  const [isResettingPassword, setIsResettingPassword] = useState(false)

  const updateUserMutation = useMutation({
    mutationFn: (request: UpdateUserRequest) => updateUser(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: idmQueryKeys.users.current()
      })
      toastSuccess('Your profile has been updated successfully.')
    },
    onError: () => {
      toastError('An error occurred while updating your profile.')
    }
  })

  const profileForm = useForm({
    initialValues: {
      FirstName: '',
      LastName: '',
      Title: '',
      Phone: ''
    },
    validate: {
      FirstName: isNotEmpty('First Name is required.'),
      LastName: isNotEmpty('Last Name is required.')
    },
    validateInputOnChange: true
  })
  const profileFormRef = useLatestRef(profileForm)

  const resetForm = useForm({
    initialValues: {
      OldPassword: '',
      NewPassword: '',
      ConfirmPassword: ''
    },
    validate: {
      OldPassword: isNotEmpty('Old Password is required.'),
      NewPassword: isNotEmpty('New Password is required.'),
      ConfirmPassword: (value, values) => {
        if (!value) return 'Confirm Password is required.'
        if (value !== values.NewPassword) return 'Passwords must match.'
        return null
      }
    }
  })

  const strength = getStrength(resetForm.values.NewPassword)
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red'
  const checks = requirements.map((requirement, index) => (
    <PasswordRequirement
      key={index}
      label={requirement.label}
      meets={requirement.re.test(resetForm.values.NewPassword)}
    />
  ))
  const resetFormIsValid =
    resetForm.values.NewPassword.length > 5 &&
    requirements.every((r) => r.re.test(resetForm.values.NewPassword)) &&
    resetForm.isValid

  const submit = async () => {
    if (!account) {
      return
    }

    updateUserMutation.mutate({
      ID: account.ID,
      FirstName: profileForm.values.FirstName,
      LastName: profileForm.values.LastName,
      Title: profileForm.values.Title,
      Phone: profileForm.values.Phone
    })
  }

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value)
    profileForm.setFieldValue('Phone', formatted)
  }

  const resetPassword = async () => {
    if (!session || !resetFormIsValid) {
      return
    }

    setIsResettingPassword(true)
    const defaultErrorMessage =
      'An unexpected error has occurred while attempting to change your password.'

    try {
      let response = await cognitoIdentityProviderClient.send(
        new ChangePasswordCommand({
          PreviousPassword: resetForm.values.OldPassword,
          ProposedPassword: resetForm.values.NewPassword,
          AccessToken: session.accessToken
        })
      )

      if (response.$metadata.httpStatusCode === 200) {
        toastSuccess('Your password has been successfully changed.')
        resetForm.reset()
      } else {
        toastError(defaultErrorMessage)
      }
    } catch (error) {
      console.error(error)
      if (error instanceof NotAuthorizedException) {
        toastError('Your old password is incorrect.')
      } else {
        toastError(defaultErrorMessage)
      }
    }
    setIsResettingPassword(false)
  }

  useEffect(() => {
    profileFormRef.current.setValues({
      FirstName: account?.FirstName ?? '',
      LastName: account?.LastName ?? '',
      Title: account?.Title ?? '',
      Phone: formatPhoneNumber(account?.Phone) ?? ''
    })
  }, [account, profileFormRef])

  return (
    <MasterPage
      breadcrumbs={[
        {
          name: 'Home',
          route: '/'
        },
        {
          name: 'My Profile',
          route: '/profile',
          active: true
        }
      ]}
      header={{
        title: 'My Profile'
      }}
    >
      {/* The page frame is viewport-high (see (main)/layout.tsx), so this
          wrapper scrolls when the three cards are taller than the window. */}
      <div className="h-full w-full overflow-y-auto pb-8">
        <Grid justify="center">
          <Grid.Col span={{ base: 12, md: 8, lg: 6, xl: 4 }}>
            <Paper p="lg" shadow="sm" withBorder>
              <form onSubmit={profileForm.onSubmit(submit)}>
                <Stack gap="md">
                  <TextInput
                    withAsterisk
                    disabled
                    label="Email"
                    value={account?.Email || ''}
                  />
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        withAsterisk
                        label="First Name"
                        {...profileForm.getInputProps('FirstName')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        withAsterisk
                        label="Last Name"
                        {...profileForm.getInputProps('LastName')}
                      />
                    </Grid.Col>
                  </Grid>

                  <Grid>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Title"
                        {...profileForm.getInputProps('Title')}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                      <TextInput
                        label="Phone"
                        {...profileForm.getInputProps('Phone')}
                        onChange={handlePhoneChange}
                      />
                    </Grid.Col>
                  </Grid>

                  <Group className={'justify-end'}>
                    <Button
                      type="submit"
                      disabled={
                        updateUserMutation.isPending ||
                        isResettingPassword ||
                        !profileForm.isValid()
                      }
                      loading={updateUserMutation.isPending}
                    >
                      Submit
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Paper>

            <Paper p="lg" shadow="sm" withBorder mt="md">
              <Stack gap="md">
                <form onSubmit={resetForm.onSubmit(resetPassword)}>
                  <Stack gap="md">
                    <Grid>
                      <Grid.Col>
                        <PasswordInput
                          withAsterisk
                          label="Old Password"
                          {...resetForm.getInputProps('OldPassword')}
                        />
                      </Grid.Col>
                      <Grid.Col>
                        <Popover
                          opened={requirementsPopoverOpened}
                          position="bottom"
                          width="target"
                          transitionProps={{ transition: 'pop' }}
                        >
                          <Popover.Target>
                            <div
                              onFocusCapture={() =>
                                setRequirementsPopoverOpened(true)
                              }
                              onBlurCapture={() =>
                                setRequirementsPopoverOpened(false)
                              }
                            >
                              <PasswordInput
                                withAsterisk
                                label="New Password"
                                {...resetForm.getInputProps('NewPassword')}
                              />
                            </div>
                          </Popover.Target>
                          <Popover.Dropdown>
                            <Progress
                              color={color}
                              value={strength}
                              size={5}
                              mb="xs"
                            />
                            <PasswordRequirement
                              label="Includes at least 6 characters"
                              meets={resetForm.values.NewPassword.length > 5}
                            />
                            {checks}
                          </Popover.Dropdown>
                        </Popover>
                      </Grid.Col>
                      <Grid.Col>
                        <PasswordInput
                          withAsterisk
                          label="Confirm Password"
                          {...resetForm.getInputProps('ConfirmPassword')}
                        />
                      </Grid.Col>
                    </Grid>
                    <Group className={'justify-end'}>
                      <Button
                        type="submit"
                        disabled={
                          updateUserMutation.isPending ||
                          isResettingPassword ||
                          !resetFormIsValid
                        }
                        loading={isResettingPassword}
                      >
                        Reset Password
                      </Button>
                    </Group>
                  </Stack>
                </form>
              </Stack>
            </Paper>

            <StatusPageEmails />
          </Grid.Col>
        </Grid>
      </div>
    </MasterPage>
  )
}
